import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { useOptions } from '../../../stateManagers/hooks/useOptions';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalSwitch } from '../../../components/GlobalComponents/GlobalSwitch';
import { GlobalDateField } from '../../../components/GlobalComponents/GlobalDateField';
import { Nullable } from 'primereact/ts-helpers';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface LocationConfig {
  lat: number;
  lng: number;
  radius: number;
  enabled: boolean;
}

interface TimeWindow {
  enabled: boolean;
  start: string; // "HH:mm" — always stored/sent as string
  end: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Defaults
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_LOCATION: LocationConfig = { lat: 0, lng: 0, radius: 100, enabled: true };
const DEFAULT_CHECKIN:  TimeWindow     = { enabled: false, start: '08:00', end: '09:30' };
const DEFAULT_CHECKOUT: TimeWindow     = { enabled: false, start: '15:00', end: '17:00' };

// ─────────────────────────────────────────────────────────────────────────────
// Time conversion helpers
// GlobalDateField (timeOnly) returns a Date object.
// The API stores/expects "HH:mm" strings.
// ─────────────────────────────────────────────────────────────────────────────

/** "HH:mm" string → Date (today's date, time set to HH:mm, local) */
function timeStrToDate(hhmm: string): Date | null {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
}

/** Date → "HH:mm" string */
function dateToTimeStr(d: Date | null | undefined): string {
  if (!d) return '';
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared styles
// ─────────────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '13px',
  outline: 'none',
  background: '#fff',
  color: '#1e293b',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: '600',
  color: '#475569',
  marginBottom: '5px',
  display: 'block',
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
};

const hintStyle: React.CSSProperties = {
  fontSize: '11px',
  color: '#94a3b8',
  marginTop: '4px',
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components (unchanged from original)
// ─────────────────────────────────────────────────────────────────────────────

const SectionHeader: React.FC<{
  iconClass: string;
  iconBg: string;
  title: string;
  badge?: React.ReactNode;
  description: string;
  controls: React.ReactNode;
  borderColor?: string;
}> = ({ iconClass, iconBg, title, badge, description, controls, borderColor = '#f1f5f9' }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '20px', paddingBottom: '16px',
    borderBottom: `2px solid ${borderColor}`,
    flexWrap: 'wrap', gap: '12px',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        width: '42px', height: '42px', borderRadius: '11px',
        background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <i className={iconClass} style={{ fontSize: '18px', color: '#fff' }} />
      </div>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>{title}</h2>
          {badge}
        </div>
        <p style={{ fontSize: '12px', color: '#64748b', margin: '3px 0 0', lineHeight: 1.5 }}>{description}</p>
      </div>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
      {controls}
    </div>
  </div>
);

const Badge: React.FC<{ active: boolean; activeLabel?: string; inactiveLabel?: string }> = ({
  active,
  activeLabel   = '✓ Active',
  inactiveLabel = '⊘ Off',
}) => (
  <span style={{
    fontSize: '11px', fontWeight: '600', borderRadius: '20px', padding: '2px 10px',
    background: active ? '#dcfce7' : '#fef9c3',
    color:      active ? '#166534' : '#854d0e',
    border:     `1px solid ${active ? '#86efac' : '#fde047'}`,
  }}>
    {active ? activeLabel : inactiveLabel}
  </span>
);

const InfoBanner: React.FC<{
  color?: string; bg?: string; border?: string; icon?: string; children: React.ReactNode;
}> = ({
  color  = '#1e40af',
  bg     = '#eff6ff',
  border = '#bfdbfe',
  icon   = 'pi pi-info-circle',
  children,
}) => (
  <div style={{ background: bg, borderRadius: '10px', padding: '12px 16px', border: `1px solid ${border}`, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
    <i className={icon} style={{ color, fontSize: '15px', marginTop: '1px', flexShrink: 0 }} />
    <p style={{ fontSize: '12px', color, margin: 0, lineHeight: 1.6 }}>{children}</p>
  </div>
);

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{
    background: '#fff', borderRadius: '16px', padding: '24px', marginBottom: '20px',
    boxShadow: '0 1px 6px rgba(15,23,42,0.07)', border: '1px solid #e2e8f0', ...style,
  }}>
    {children}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const AttendanceSetupSettings: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [savingLoc, setSavingLoc] = useState(false);
  const [savingWin, setSavingWin] = useState(false);
  const [savingGps, setSavingGps] = useState(false);

  // Local form state — populated from useOptions on first load
  const [location,       setLocation]       = useState<LocationConfig>(DEFAULT_LOCATION);
  const [checkinWindow,  setCheckinWindow]   = useState<TimeWindow>(DEFAULT_CHECKIN);
  const [checkoutWindow, setCheckoutWindow]  = useState<TimeWindow>(DEFAULT_CHECKOUT);
  const [gpsOnlyEnabled, setGpsOnlyEnabled]  = useState(false);

  // Track whether the form has been seeded from the API
  const [seeded, setSeeded] = useState(false);

  // ── Single useOptions call for all four config keys ───────────────────────
  // Replaces three separate apiClient.get() calls:
  //   /options/attendance_location
  //   /multiple-options?names=checkin_window,checkout_window
  //   /multiple-options?names=gps_only_attendance
  const { options, loading } = useOptions<any>([
    'attendance_location',
    'checkin_window',
    'checkout_window',
    'gps_only_attendance',
  ]);

  // Seed local state once options have loaded
  useEffect(() => {
    if (loading || seeded) return;

    const locData   = options['attendance_location']?.data  as LocationConfig | null;
    const ciData    = options['checkin_window']?.data        as TimeWindow     | null;
    const coData    = options['checkout_window']?.data       as TimeWindow     | null;
    const gpsData   = options['gps_only_attendance']?.data   as { enabled: boolean } | null;

    if (locData) setLocation(locData);
    if (ciData)  setCheckinWindow(ciData);
    if (coData)  setCheckoutWindow(coData);
    if (gpsData) setGpsOnlyEnabled(!!gpsData.enabled);

    setSeeded(true);
  }, [loading, seeded, options]);

  // GPS-only requires both windows — auto-disable if either is turned off
  const canEnableGpsOnly = checkinWindow.enabled && checkoutWindow.enabled;
  useEffect(() => {
    if (!canEnableGpsOnly && gpsOnlyEnabled) setGpsOnlyEnabled(false);
  }, [canEnableGpsOnly]);

  // ── Save: School Location ─────────────────────────────────────────────────
  // Location is its own dedicated option key — saved alone via multiple-options-v2.
  const handleSaveLocation = async (): Promise<void> => {
    if (!location.lat || !location.lng) {
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Latitude and longitude are required', life: 4000 });
      return;
    }
    if (location.radius < 10 || location.radius > 5000) {
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Radius must be between 10 and 5,000 metres', life: 4000 });
      return;
    }

    setSavingLoc(true);
    try {
      const res = await apiClient.post('/multiple-options-v2', {
        attendance_location: {
          lat:     location.lat,
          lng:     location.lng,
          radius:  location.radius,
          enabled: location.enabled,
        },
      });

      if (res.data.status === 'error') throw new Error(res.data.message || 'Failed to save');
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'School location saved', life: 3000 });
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || 'Failed to save location', life: 5000 });
    } finally {
      setSavingLoc(false);
    }
  };

  // ── Save: Time Windows ────────────────────────────────────────────────────
  // Saves checkin_window + checkout_window in ONE request to multiple-options-v2.
  const handleSaveWindows = async (): Promise<void> => {
    for (const [label, win] of [['Check-in', checkinWindow], ['Check-out', checkoutWindow]] as const) {
      if (win.enabled) {
        if (!win.start || !win.end) {
          toast.current?.show({ severity: 'warn', summary: 'Validation', detail: `${label}: start and end times are required`, life: 4000 });
          return;
        }
        if (win.start >= win.end) {
          toast.current?.show({ severity: 'warn', summary: 'Validation', detail: `${label}: start must be before end`, life: 4000 });
          return;
        }
      }
    }

    setSavingWin(true);
    try {
      const res = await apiClient.post('/multiple-options-v2', {
        checkin_window:  checkinWindow,
        checkout_window: checkoutWindow,
      });

      if (res.data.status === 'error') throw new Error('Failed to save time windows');
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Time windows saved', life: 3000 });
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || 'Failed to save time windows', life: 5000 });
    } finally {
      setSavingWin(false);
    }
  };

  // ── Save: GPS-Only ────────────────────────────────────────────────────────
  // Saved alone so the toggle responds instantly without re-saving windows.
  const handleSaveGpsOnly = async (): Promise<void> => {
    setSavingGps(true);
    try {
      const res = await apiClient.post('/multiple-options-v2', {
        gps_only_attendance: { enabled: gpsOnlyEnabled },
      });

      if (res.data.status === 'error') throw new Error('Failed to save GPS setting');
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: `GPS-only attendance ${gpsOnlyEnabled ? 'enabled' : 'disabled'}`, life: 3000 });
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || 'Failed to save GPS-only setting', life: 5000 });
    } finally {
      setSavingGps(false);
    }
  };

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading && !seeded) {
    return (
      <Card style={{ textAlign: 'center', padding: '60px 24px' }}>
        <div style={{
          width: '38px', height: '38px',
          border: '3px solid #e2e8f0', borderTop: '3px solid #3b82f6',
          borderRadius: '50%', animation: 'spin 0.9s linear infinite', margin: '0 auto 14px',
        }} />
        <p style={{ color: '#64748b', fontSize: '14px' }}>Loading attendance settings…</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </Card>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Toast ref={toast} position="top-right" />

      {/* ── School Location ──────────────────────────────────────────────── */}
      <Card>
        <SectionHeader
          iconClass="pi pi-map-marker"
          iconBg="linear-gradient(135deg,#3b82f6,#60a5fa)"
          title="School Location"
          badge={<Badge active={location.enabled} activeLabel="✓ Active" inactiveLabel="⊘ Disabled" />}
          description="GPS coordinates and radius used to validate staff are on-site"
          controls={
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                <span style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>
                  {location.enabled ? 'Enabled' : 'Disabled'}
                </span>
                <GlobalSwitch
                  value={location.enabled}
                  onChange={() => setLocation(p => ({ ...p, enabled: !p.enabled }))}
                />
              </div>
              <GlobalButton
                label={savingLoc ? 'Saving…' : 'Save Location'}
                onClick={handleSaveLocation}
                disabled={savingLoc}
                loading={savingLoc}
                icon={savingLoc ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                variant="success"
                size="small"
              />
            </>
          }
        />

        <InfoBanner>
          Staff within the radius are marked <strong>present</strong> immediately. Those outside receive a{' '}
          <strong>location-pass barcode</strong> for admin override.
        </InfoBanner>

        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginTop: '18px',
          opacity: location.enabled ? 1 : 0.4,
          pointerEvents: location.enabled ? 'all' : 'none',
          transition: 'opacity 0.2s',
        }}>
          <div>
            <label style={labelStyle}>Latitude</label>
            <input
              type="number" step="0.000001" placeholder="e.g. 28.613940"
              value={location.lat || ''}
              onChange={e => setLocation(p => ({ ...p, lat: parseFloat(e.target.value) }))}
              style={inputStyle}
            />
            <p style={hintStyle}>Decimal degrees (−90 to 90)</p>
          </div>
          <div>
            <label style={labelStyle}>Longitude</label>
            <input
              type="number" step="0.000001" placeholder="e.g. 77.209023"
              value={location.lng || ''}
              onChange={e => setLocation(p => ({ ...p, lng: parseFloat(e.target.value) }))}
              style={inputStyle}
            />
            <p style={hintStyle}>Decimal degrees (−180 to 180)</p>
          </div>
          <div>
            <label style={labelStyle}>Allowed Radius (metres)</label>
            <input
              type="number" min="10" max="5000" placeholder="e.g. 100"
              value={location.radius || ''}
              onChange={e => setLocation(p => ({ ...p, radius: parseInt(e.target.value) }))}
              style={inputStyle}
            />
            <p style={hintStyle}>Min 10 m · max 5,000 m</p>
          </div>
        </div>

        {location.lat !== 0 && location.lng !== 0 && (
          <div style={{ marginTop: '14px', padding: '9px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px', color: '#334155', fontFamily: 'monospace' }}>
            {location.lat.toFixed(6)}, {location.lng.toFixed(6)} — within {location.radius} m
          </div>
        )}
      </Card>

      {/* ── GPS-Only Attendance ───────────────────────────────────────────── */}
      <Card style={{ border: gpsOnlyEnabled ? '1px solid #86efac' : '1px solid #e2e8f0', transition: 'border-color 0.2s' }}>
        <SectionHeader
          iconClass={gpsOnlyEnabled ? 'pi pi-wifi' : 'pi pi-ban'}
          iconBg={gpsOnlyEnabled ? 'linear-gradient(135deg,#10b981,#34d399)' : 'linear-gradient(135deg,#94a3b8,#cbd5e1)'}
          title="GPS-Only Attendance"
          badge={
            !canEnableGpsOnly
              ? <span style={{ fontSize: '11px', fontWeight: '600', borderRadius: '20px', padding: '2px 10px', background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}>🔒 Requires Time Windows</span>
              : <Badge active={gpsOnlyEnabled} activeLabel="✓ Enabled" inactiveLabel="⊘ Barcode Required" />
          }
          description={
            gpsOnlyEnabled
              ? 'Staff can mark attendance using GPS location alone — no barcode scan needed'
              : 'Barcode scan is required for attendance; GPS alone is not accepted'
          }
          controls={
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                <span style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>
                  {gpsOnlyEnabled ? 'On' : 'Off'}
                </span>
                <GlobalSwitch
                  value={gpsOnlyEnabled}
                  onChange={() => setGpsOnlyEnabled(p => !p)}
                  disabled={!canEnableGpsOnly}
                />
              </div>
              <GlobalButton
                label={savingGps ? 'Saving…' : 'Save'}
                onClick={handleSaveGpsOnly}
                disabled={savingGps || !canEnableGpsOnly}
                loading={savingGps}
                icon={savingGps ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                variant="success"
                size="small"
              />
            </>
          }
          borderColor={gpsOnlyEnabled ? '#86efac' : '#f1f5f9'}
        />

        {!canEnableGpsOnly && (
          <InfoBanner color="#92400e" bg="#fffbeb" border="#fcd34d" icon="pi pi-lock">
            <strong>GPS-Only attendance is locked.</strong> Enable both Check-in and Check-out time windows first.
          </InfoBanner>
        )}

        {canEnableGpsOnly && (
          <div style={{ opacity: gpsOnlyEnabled ? 1 : 0.45, pointerEvents: gpsOnlyEnabled ? 'all' : 'none', transition: 'opacity 0.2s' }}>
            {gpsOnlyEnabled ? (
              <InfoBanner color="#065f46" bg="#f0fdf4" border="#86efac" icon="pi pi-check-circle">
                <strong>GPS-Only mode is active.</strong> Staff must be within the allowed radius. Outside-radius attempts will be rejected.
              </InfoBanner>
            ) : (
              <InfoBanner color="#78350f" bg="#fffbeb" border="#fde047" icon="pi pi-exclamation-triangle">
                GPS-Only is <strong>disabled</strong>. Staff must scan the rotating barcode to mark attendance.
              </InfoBanner>
            )}
          </div>
        )}
      </Card>

      {/* ── Scan Time Windows ─────────────────────────────────────────────── */}
      <Card>
        <SectionHeader
          iconClass="pi pi-clock"
          iconBg="linear-gradient(135deg,#8b5cf6,#a78bfa)"
          title="Scan Time Windows"
          description="Restrict the hours during which check-in and check-out scans are accepted"
          controls={
            <GlobalButton
              label={savingWin ? 'Saving…' : 'Save Windows'}
              onClick={handleSaveWindows}
              disabled={savingWin}
              loading={savingWin}
              icon={savingWin ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
              variant="success"
              size="small"
            />
          }
        />

        {gpsOnlyEnabled && (
          <div style={{ marginBottom: '16px' }}>
            <InfoBanner color="#065f46" bg="#f0fdf4" border="#86efac" icon="pi pi-wifi">
              <strong>GPS-Only mode is active</strong> and depends on these windows. Disabling either window will automatically turn off GPS-Only attendance.
            </InfoBanner>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {([
            {
              type: 'checkin'  as const,
              win: checkinWindow,
              setWin: setCheckinWindow,
              label: 'Check-in',
              iconBg: 'linear-gradient(135deg,#22c55e,#4ade80)',
              icon: 'pi pi-sign-in',
            },
            {
              type: 'checkout' as const,
              win: checkoutWindow,
              setWin: setCheckoutWindow,
              label: 'Check-out',
              iconBg: 'linear-gradient(135deg,#f97316,#fb923c)',
              icon: 'pi pi-sign-out',
            },
          ] as const).map(({ type, win, setWin, label, iconBg, icon }) => (
            <div
              key={type}
              style={{
                border: `1px solid ${win.enabled ? '#e2e8f0' : '#fde047'}`,
                borderRadius: '12px', padding: '18px',
                background: win.enabled ? '#fafafa' : '#fffdf0',
                transition: 'border-color 0.2s, background 0.2s',
              }}
            >
              {/* Card header row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '34px', height: '34px', borderRadius: '9px',
                    background: win.enabled ? iconBg : 'linear-gradient(135deg,#94a3b8,#cbd5e1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background 0.2s', flexShrink: 0,
                  }}>
                    <i className={win.enabled ? icon : 'pi pi-clock'} style={{ fontSize: '15px', color: '#fff' }} />
                  </div>
                  <div>
                    <p style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', margin: 0 }}>{label}</p>
                    <Badge active={win.enabled} activeLabel="Enforced" inactiveLabel="Any time" />
                  </div>
                </div>
                <GlobalSwitch
                  value={win.enabled}
                  onChange={() => setWin(p => ({ ...p, enabled: !p.enabled }))}
                />
              </div>

              {/* Time pickers — disabled/faded when window is off */}
              <div style={{
                opacity: win.enabled ? 1 : 0.4,
                pointerEvents: win.enabled ? 'all' : 'none',
                transition: 'opacity 0.2s',
              }}>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {/* ── Start time — GlobalDateField (timeOnly) ── */}
                  <div style={{ flex: 1 }}>
                    <GlobalDateField
                      label="Start"
                      timeOnly
                      showTime
                      dateFormat="HH:mm"
                      value={timeStrToDate(win.start)}
                      onChange={(v: Nullable<Date | Date[] | (Date | null)[]>) => {
                        const date = Array.isArray(v) ? v[0] : v;
                        setWin(p => ({ ...p, start: dateToTimeStr(date as Date | null) }));
                      }}
                      placeholder="08:00"
                      icon="pi pi-clock"
                      disabled={!win.enabled}
                    />
                  </div>

                  {/* ── End time — GlobalDateField (timeOnly) ── */}
                  <div style={{ flex: 1 }}>
                    <GlobalDateField
                      label="End"
                      timeOnly
                      showTime
                      dateFormat="HH:mm"
                      value={timeStrToDate(win.end)}
                      onChange={(v: Nullable<Date | Date[] | (Date | null)[]>) => {
                        const date = Array.isArray(v) ? v[0] : v;
                        setWin(p => ({ ...p, end: dateToTimeStr(date as Date | null) }));
                      }}
                      placeholder="09:30"
                      icon="pi pi-clock"
                      disabled={!win.enabled}
                    />
                  </div>
                </div>

                {/* Active window summary */}
                {win.start && win.end && win.enabled && (
                  <div style={{ marginTop: '10px', background: '#eff6ff', borderRadius: '8px', padding: '8px 12px', border: '1px solid #bfdbfe', fontSize: '12px', color: '#1e40af' }}>
                    <i className="pi pi-info-circle" style={{ marginRight: '6px' }} />
                    Scans accepted <strong>{win.start}</strong> – <strong>{win.end}</strong>
                  </div>
                )}
              </div>

              {/* Disabled window warning */}
              {!win.enabled && (
                <div style={{ marginTop: '10px', background: '#fffbeb', borderRadius: '8px', padding: '8px 12px', border: '1px solid #fde047', fontSize: '12px', color: '#78350f' }}>
                  <i className="pi pi-exclamation-triangle" style={{ marginRight: '6px' }} />
                  {label} has <strong>no time restriction</strong>.
                  {gpsOnlyEnabled && (
                    <span style={{ display: 'block', marginTop: '4px', color: '#b45309' }}>
                      ⚠ Disabling this will turn off GPS-Only attendance.
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ marginTop: '16px' }}>
          <InfoBanner>
            Scans outside the enabled window are <strong>rejected</strong>. Disable a window to allow scanning at any hour. Both windows save together with the button above.
          </InfoBanner>
        </div>
      </Card>
    </>
  );
};

export default AttendanceSetupSettings;